import api from './api';
import {
  Blog,
  CreateBlogRequest,
  UpdateBlogRequest,
  ApproveBlogRequest,
  RejectBlogRequest,
  PaginatedResponse,
  BlogStatus,
} from '../types/blog';

// Public APIs (no authentication)
export const blogPublicApi = {
  // Get all published blogs
  getAll: async (
    page = 0,
    size = 20,
    search?: string,
    tag?: string
  ): Promise<PaginatedResponse<Blog>> => {
    const response = await api.get('/blogs', {
      params: { page, size, search, tag },
    });
    const data = response.data;
    if (data.content) {
      return data;
    }
    return {
      content: Array.isArray(data) ? data : [],
      totalElements: Array.isArray(data) ? data.length : 0,
      totalPages: 1,
      currentPage: page,
      size,
    };
  },

  // Get single published blog
  getById: async (id: number): Promise<Blog> => {
    const response = await api.get(`/blogs/${id}`);
    return response.data;
  },
};

// User/Technician APIs (authentication required)
export const blogUserApi = {
  // Create blog
  create: async (data: CreateBlogRequest, images?: File[], documents?: File[]): Promise<Blog> => {
    if (images && images.length > 0 || documents && documents.length > 0) {
      // Multipart form data
      const formData = new FormData();
      formData.append('title', data.title);
      formData.append('content', data.content);
      if (data.summary) formData.append('summary', data.summary);
      if (data.tags && data.tags.length > 0) formData.append('tags', data.tags.join(','));
      if (images) images.forEach(img => formData.append('images', img));
      if (documents) documents.forEach(doc => formData.append('documents', doc));

      const response = await api.post('/blogs', formData);
      return response.data;
    } else {
      // JSON
      const response = await api.post('/blogs', data);
      return response.data;
    }
  },

  // Get my blogs
  getMyBlogs: async (): Promise<Blog[]> => {
    const response = await api.get('/blogs/my-blogs');
    return Array.isArray(response.data) ? response.data : [];
  },

  // Update my blog
  update: async (id: number, data: UpdateBlogRequest, images?: File[], documents?: File[]): Promise<Blog> => {
    if (images && images.length > 0 || documents && documents.length > 0) {
      // Multipart form data
      const formData = new FormData();
      if (data.title) formData.append('title', data.title);
      if (data.content) formData.append('content', data.content);
      if (data.summary) formData.append('summary', data.summary);
      if (data.tags && data.tags.length > 0) formData.append('tags', data.tags.join(','));
      if (images) images.forEach(img => formData.append('images', img));
      if (documents) documents.forEach(doc => formData.append('documents', doc));

      const response = await api.put(`/blogs/${id}`, formData);
      return response.data;
    } else {
      // JSON
      const response = await api.put(`/blogs/${id}`, data);
      return response.data;
    }
  },

  // Delete my blog
  delete: async (id: number): Promise<void> => {
    await api.delete(`/blogs/${id}`);
  },

  // Upload images to my blog
  uploadImages: async (id: number, images: File[]): Promise<Blog> => {
    const formData = new FormData();
    images.forEach(image => formData.append('images', image));

    const response = await api.post(`/blogs/${id}/images`, formData);
    return response.data;
  },

  // Upload documents to my blog
  uploadDocuments: async (id: number, documents: File[]): Promise<Blog> => {
    const formData = new FormData();
    documents.forEach(doc => formData.append('documents', doc));

    const response = await api.post(`/blogs/${id}/documents`, formData);
    return response.data;
  },
};

// Admin APIs (authentication required)
export const blogAdminApi = {
  // Get all blogs (all statuses)
  getAll: async (
    page = 0,
    size = 20,
    search?: string,
    status?: BlogStatus
  ): Promise<PaginatedResponse<Blog>> => {
    const response = await api.get('/blogs/admin/all', {
      params: { page, size, search, status },
    });
    const data = response.data;
    if (data.content) {
      return data;
    }
    return {
      content: Array.isArray(data) ? data : [],
      totalElements: Array.isArray(data) ? data.length : 0,
      totalPages: 1,
      currentPage: page,
      size,
    };
  },

  // Get pending blogs
  getPending: async (): Promise<Blog[]> => {
    const response = await api.get('/blogs/pending');
    return Array.isArray(response.data) ? response.data : [];
  },

  // Approve blog
  approve: async (id: number, data?: ApproveBlogRequest): Promise<Blog> => {
    const response = await api.put(`/blogs/${id}/approve`, data || {});
    return response.data;
  },

  // Reject blog
  reject: async (id: number, data: RejectBlogRequest): Promise<Blog> => {
    const response = await api.put(`/blogs/${id}/reject`, data);
    return response.data;
  },

  // Update any blog (admin can update without changing status)
  update: async (id: number, data: UpdateBlogRequest): Promise<Blog> => {
    const response = await api.put(`/blogs/${id}`, data);
    return response.data;
  },

  // Delete any blog
  delete: async (id: number): Promise<void> => {
    await api.delete(`/blogs/${id}`);
  },
};

